import React from 'react';
import '@/pages/ventures/Venture.scss';
import PageBanner from '@/components/ui/PageBanner';
import realestate from '@/assets/real-estate-banner.jpeg';
import a1 from '@/assets/properties/a1.jpg';
import a2 from '@/assets/properties/a2.jpg';
import d1 from '@/assets/properties/d1.jpeg';
import d2 from '@/assets/properties/d2.jpeg';
import l1 from '@/assets/properties/l1.jpg';
import l2 from '@/assets/properties/l2.jpeg';
import m1 from '@/assets/properties/m1.jpeg';
import m2 from '@/assets/properties/m2.jpeg';
import BodyMotion from '@/components/ui/BodyMotion';
import ImageBanner from '@/components/sections/ImageBanner';
import amsterdam from '@/assets/dayshots/amsterdam.jpg';
import dublin from '@/assets/dayshots/dublin.jpg';
import lisbon from '@/assets/dayshots/lisbon.jpg';
import manchester from '@/assets/dayshots/manchester.jpg';
import ComparisonSlider from '@/components/ui/ComparisonSlider';
import useHashScroll from '@/hooks/useHashScroll';
import useActiveSection from '@/hooks/useActiveSection';

const RealEstateHospitality: React.FC = () => {
  useHashScroll();
  const sectionIds = React.useMemo(() => ['lisbon', 'amsterdam', 'dublin', 'manchester'], []);
  useActiveSection(sectionIds);

  return (
    <main className="venture-page">
      <PageBanner title="International Real Estate & Hospitality" image={realestate} />
      <section className="venture-page__content">
        <BodyMotion>
          <p>
            At Ephemera, our conviction is that profound technological disruption of international real estate and hospitality is beginning. Artificial intelligence is learning to see space as we do, developing a deeper understanding of light, orientation, and location. It is increasingly possible from anywhere in the world to discover and value the emotional characteristics that give real estate soul: light, period details, craftsmanship, and how the sun moves across a terrace.
          </p>
          <p>
            Today, the ranking of hospitality listings is primarily quantitative based on the scores assigned by guest reviews.  Soon, reviews themselves will become structured data, filtered not only by light, design and individuality, but also by the warmth of the hospitality experience: attentiveness, authenticity and the curation of enriching experiences at the destination.  Algorithms will prioritize listings that consistently deliver authentically differentiated experiences, facilitating their ability to command an increasing premium.
          </p>
          <p>
           The ability to access and assess these dynamics from the United States has never been higher.  For the first time detailed initial multi-jurisdictional legal, tax and regulatory information is available on our smartphones.  While on-the-ground expertise and counsel remains indispensable, a decent understanding of the shape of the playing field and the rules of engagement is now just a few prompts away.  
          </p>
          <p>
             For more than two decades, our principals have been guided by these criteria in their international real estate activity. After identifying a specific neighbourhood within a city, light is the foundational quality for our investment approach. Once we invest, the asset is enhanced under local architectural supervision to showcase the essence that makes it unique.  
          </p>
          <p>
            The finished product is then opened to select guests on technological platforms for residencies of varying tenors depending on the characteristics and regulatory environment of its location.  We strive for  a personalized experience to facilitate the creation of lifelong memories in the world’s great cities.
          </p>
        </BodyMotion>
      </section>

      <section id="lisbon" className="venture-page__city">
        <ImageBanner city="Lisbon" image={lisbon} />
        <div className="venture-page__city-gap">
          <ComparisonSlider
            text={`The sunlight reflecting off the Atlantic Ocean onto pastel-hued façades creates a magical reverence that makes Lisbon unique. Steeped in the history of explorers who fearlessly sought new horizons, it now attracts a new breed of modern-day explorers: those who can work from anywhere and who prioritize the highest quality of life when deciding on residency location.`}
            imagePath={l1}
          />
        </div>
        <div className="venture-page__city-gap">
          <ComparisonSlider
            text={`Lisbon offers security, sunshine, incredible natural beauty, history, architecture, unique cuisine, a vibrant cultural life, a relaxed, family-oriented beach culture and a privileged location at the westernmost point of Europe a short direct flight from multiple US cities. When these attributes are combined with the attractive incentives offered to new residents the case for Lisbon is compelling.`}
            imagePath={l2}
            textRight
          />
        </div>
      </section>

      <section id="amsterdam" className="venture-page__city">
        <ImageBanner city="Amsterdam" image={amsterdam} />
        <div className="space">
          <ComparisonSlider
            text={`Amsterdam’s creators had the vision to design one of the most unique central urban environments in the world - the UNESCO World Heritage grachtengordel, or canal belt. Even in the downtown core, stillness endures as water flows silently through the canals.  Yet Amsterdam has consistently evolved to meet the moment, and it continues to offer an array of advanced employment opportunities for local and international residents in the industries of the future.`}
            imagePath={a1}
          />
        </div>
        <div className="space">
          <ComparisonSlider
            text={`From an investment perspective, the canal belt exhibits uniquely constrained characteristics.  Its beauty and architectural quality simply cannot be replicated.  The district is expected to continue to draw aspirational residents forging successful careers for generations to come.`}
            imagePath={a2}
            textRight
          />
        </div>
      </section>

      <section id="dublin" className="venture-page__city">
        <ImageBanner city="Dublin" image={dublin} />
        <div className="space">
          <ComparisonSlider
            text={`Famed the world over, the friendliness and hospitality of Dubliners is unrivalled. These qualities define the city’s character and draw people from around the world in ever greater numbers. Layered on to its appeal are its unique attributes as the only sizeable native English-speaking EU country, predictable and transparent regulatory policy and a deep pool of talented, well-educated residents.`}
            imagePath={d1}
          />
        </div>
        <div className="space">
          <ComparisonSlider
            text={`As a low-rise city with limited public transportation and heavy traffic, proximity and location are key.  Our investment focus is on vibrant walkable neighborhoods where daily life unfolds within a short stroll not only of cafés, parks, and creative spaces, but also the EU headquarters of the many US and other multinationals who continue to make Dublin their European home base.`}
            imagePath={d2}
            textRight
          />
        </div>
      </section>

      <section id="manchester" className="venture-page__city">
        <ImageBanner city="Manchester" image={manchester} />
        <div className="space">
          <ComparisonSlider
            text={`Manchester has for centuries been at the forefront of cultural change. Once the engine of the Industrial Revolution, it gave us the legendary Haçienda and the music of The Stone Roses and Oasis, among countless others.`}
            imagePath={m1}
          />
        </div>
        <div className="space">
          <ComparisonSlider
            text={`This energy was harnessed into the regeneration of city center in recent decades creating an urban living environment steeped in vibrant cultural energy.  It is our thesis that the ecosystem of city center living in Manchester is self-sustaining and will continue to thrive in the coming decades.`}
            imagePath={m2}
            textRight
          />
        </div>
      </section>
    </main>
  );
};

export default RealEstateHospitality;
